<template>
  <v-dialog
    :value="value"
    max-width="480"
    scrollable
    content-class="sw-nt-dialog-wrap"
    @input="$emit('input', $event)"
  >
    <v-card class="sw-nt-card">
      <v-card-title class="sw-nt-title pb-2">
        <span>{{ $t('FieldSurveyForm.newTechnicianTitle') }}</span>
        <v-spacer />
        <v-btn icon small :aria-label="$t('FieldSurveyForm.cancel')" :disabled="saving" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-2">
        <v-form ref="form" v-model="formValid">
          <v-text-field
            v-model="name"
            :label="`${$t('NewUser.name')} *`"
            outlined
            dense
            hide-details="auto"
            background-color="#FFF"
            color="#0098E1"
            maxlength="50"
            class="mb-3"
            :rules="$ponceConfig.requiredRules"
            @blur="parseNames"
          />
          <v-text-field
            v-model="lastName"
            :label="`${$t('NewUser.lastName')} *`"
            outlined
            dense
            hide-details="auto"
            background-color="#FFF"
            color="#0098E1"
            maxlength="50"
            class="mb-3"
            :rules="$ponceConfig.requiredRules"
            @blur="parseNames"
          />
          <v-text-field
            v-model="email"
            :label="`${$t('FieldSurveyForm.newTechnicianEmail')} *`"
            type="email"
            outlined
            dense
            hide-details="auto"
            background-color="#FFF"
            color="#0098E1"
            class="mb-3"
            :rules="$ponceConfig.emailRules"
          />
          <div class="sw-nt-phone">
            <VuePhoneNumberInput
              v-model="phoneNumberInput"
              color="#0098E1"
              size="lg"
              default-country-code="AR"
              :translations="phoneInputTranslations"
              @update="setPhoneNumber"
            />
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn text class="text-none" color="grey darken-1" :disabled="saving" @click="close">
          {{ $t('FieldSurveyForm.cancel') }}
        </v-btn>
        <v-btn
          color="#0098E1"
          depressed
          class="sw-nt-save-btn text-none"
          :loading="saving"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ $t('FieldSurveyForm.newTechnicianSave') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import capitalize from 'lodash/capitalize'
import { createFieldSurveyTechnicianUser } from '@/lib/crudOperations/write'

export default {
  name: 'SurveyWorkNewTechnicianDialog',
  components: { VuePhoneNumberInput },
  props: {
    value: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      formValid: false,
      name: '',
      lastName: '',
      email: '',
      phoneNumberInput: '',
      phoneFormatted: '',
      isPhoneValid: false,
      saving: false,
      phoneInputTranslations: {
        countrySelectorLabel: this.$t('NewUser.countryCode'),
        phoneNumberLabel: `${this.$t('NewUser.phoneNumber')} *`,
        example: this.$t('NewUser.example')
      }
    }
  },
  computed: {
    canSubmit () {
      return this.formValid && this.isPhoneValid && !this.saving
    }
  },
  watch: {
    value (open) {
      if (open) this.resetForm()
    }
  },
  methods: {
    close () {
      this.$emit('input', false)
    },
    resetForm () {
      this.name = ''
      this.lastName = ''
      this.email = ''
      this.phoneNumberInput = ''
      this.phoneFormatted = ''
      this.isPhoneValid = false
      this.saving = false
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation()
      })
    },
    setPhoneNumber (input) {
      this.isPhoneValid = !!(input && input.isValid)
      if (input && input.formattedNumber) {
        this.phoneFormatted = input.formattedNumber
      } else {
        this.phoneFormatted = ''
      }
    },
    parseNames () {
      const cap = (s) => s && s.replace(/\s+/g, ' ').split(' ').map((w) => capitalize(w)).join(' ')
      this.name = cap(this.name) || ''
      this.lastName = cap(this.lastName) || ''
    },
    async submit () {
      if (!this.canSubmit) return
      this.saving = true
      try {
        const userId = await createFieldSurveyTechnicianUser({
          name: String(this.name).trim(),
          lastName: String(this.lastName).trim(),
          phone: this.phoneFormatted || this.phoneNumberInput,
          email: String(this.email).trim()
        })
        this.$emit('created', {
          id: userId,
          name: String(this.name).trim(),
          lastName: String(this.lastName).trim()
        })
        this.close()
      } catch (e) {
        const code = e && e.code
        if (code === 'PHONE_EXISTS') {
          this.$root.snack && this.$root.snack.show({ text: this.$t('UI.phoneAlreadyExists'), error: true })
        } else {
          console.error('createFieldSurveyTechnicianUser:', e)
          this.$root.snack && this.$root.snack.show({ text: this.$t('FieldSurveyForm.newTechnicianError'), error: true })
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped lang="stylus">
.sw-nt-title
  font-size 1.1rem
  font-weight 500

.sw-nt-phone
  margin-top 8px

.sw-nt-card
  border-radius 8px

/* White label without `dark` (avoids theme glitches with custom hex) */
.sw-nt-save-btn:not(.v-btn--disabled),
.sw-nt-save-btn.v-btn--loading
  color #fff !important

.sw-nt-save-btn:not(.v-btn--disabled) >>> .v-btn__content,
.sw-nt-save-btn.v-btn--loading >>> .v-btn__content
  color #fff !important

/* Custom hex color + depressed: Vuetify leaves disabled state nearly white — force muted grey */
.sw-nt-save-btn.v-btn--disabled:not(.v-btn--loading)
  background-color #e0e4e8 !important
  color #616161 !important
  opacity 1 !important

.sw-nt-save-btn.v-btn--disabled:not(.v-btn--loading)::before
  opacity 0 !important

/* Loading: same issue as disabled — keep primary fill and visible spinner (custom hex) */
.sw-nt-save-btn.v-btn--loading
  background-color #0098E1 !important
  color #fff !important
  opacity 1 !important

.sw-nt-save-btn.v-btn--loading::before
  opacity 0 !important

.sw-nt-save-btn.v-btn--loading >>> .v-btn__loader .v-progress-circular
  color #fff !important
</style>



// WEBPACK FOOTER //
// SurveyWorkNewTechnicianDialog.vue?40eabde7